import React, { useState, useEffect } from 'react';
import * as Icons from 'lucide-react';
import { api } from '../utils/api';
import EmailGate from '../components/EmailGate';
import '../styles/wne.css';

const WNEPage = () => {
  const [showEmailGate, setShowEmailGate] = useState(false);
  const [linksConfig, setLinksConfig] = useState(null);
  const [currentLink, setCurrentLink] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const config = await api.getConfig();
      setLinksConfig(config);
    } catch (error) {
      console.error('Failed to load config:', error);
    } finally {
      setLoading(false);
    }
  };

  const appendUTM = (url) => {
    if (!linksConfig || !linksConfig.utm) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${linksConfig.utm}`;
  };

  const handleLinkClick = async (label, url) => {
    await api.trackEvent({
      event: 'link_click',
      label,
      url,
      timestamp: new Date().toISOString()
    });
  };

  const handleLinkAction = async (link) => {
    // If link has a gate, show the gate
    if (link.has_gate) {
      setCurrentLink(link);
      setShowEmailGate(true);
      await api.trackEvent({
        event: 'gate_shown',
        label: link.title,
        timestamp: new Date().toISOString()
      });
    } else {
      // Direct link - open immediately
      const finalUrl = appendUTM(link.url);
      await handleLinkClick(link.title, finalUrl);
      window.open(finalUrl, '_blank');
    }
  };

  const handleEmailGateSubmit = async (formData) => {
    try {
      if (!currentLink) return;

      // Capture lead with all provided fields
      await api.captureLead({
        name: formData.name || null,
        email: formData.email || null,
        phone: formData.phone || null,
        linkedin: formData.linkedin || null,
        resource: currentLink.gate_resource_name || currentLink.title
      });

      // Track successful lead capture
      await api.trackEvent({
        event: 'lead_captured',
        label: currentLink.title,
        timestamp: new Date().toISOString()
      });

      // Close gate
      setShowEmailGate(false);

      // Open link in new tab
      const finalUrl = appendUTM(currentLink.url);
      await handleLinkClick(currentLink.title, finalUrl);
      window.open(finalUrl, '_blank');
      
      setCurrentLink(null);
    } catch (error) {
      console.error('Failed to submit form:', error);
      throw error;
    }
  };

  const getIcon = (iconName) => {
    const Icon = Icons[iconName] || Icons.ExternalLink;
    return Icon;
  };

  if (loading) {
    return (
      <div className="wne-container">
        <div className="wne-content">
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!linksConfig) {
    return null;
  }

  // Filter enabled links and sort by order
  const enabledLinks = (linksConfig.links || [])
    .filter(link => link.enabled !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="wne-container">
      <div className="wne-content">
        {/* Header */}
        <header className="wne-header">
          <div className="logo-container">
            <img 
              src="https://customer-assets.emergentagent.com/job_wne-connects/artifacts/eu18fa7y_CEEF%20Logo%20V4%20Seal%20WHT.png" 
              alt="CEEF Logo" 
              className="logo-image logo-ceef"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_wne-connects/artifacts/ctlv6xd5_CEE%20LLC%20Logo%20V4%20WHT.png" 
              alt="CEE LLC Logo" 
              className="logo-image logo-cee"
            />
          </div>
          <h1 className="wne-title">{linksConfig.title || "Congressional Energy Engagement Group"}</h1>
          <p className="wne-tagline">{linksConfig.tagline || "Advancing Energy. Strengthening America. Leading the World."}</p>
        </header>

        {/* Dynamic Action Buttons */}
        <div className="action-buttons">
          {enabledLinks.map((link) => {
            const IconComponent = getIcon(link.icon);
            const ArrowIcon = Icons.ExternalLink;
            const themeClass = `theme-${link.color_theme || 'blue'}`;

            if (link.has_gate) {
              // Button with gate
              return (
                <button
                  key={link.id}
                  className={`action-btn ${themeClass}`}
                  onClick={() => handleLinkAction(link)}
                  role="button"
                >
                  <IconComponent className="btn-icon" size={24} />
                  <span className="btn-text">{link.title}</span>
                  <ArrowIcon className="btn-arrow" size={18} />
                </button>
              );
            } else {
              // Direct link
              return (
                <a
                  key={link.id}
                  href={appendUTM(link.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`action-btn ${themeClass}`}
                  onClick={() => handleLinkClick(link.title, link.url)}
                  role="button"
                >
                  <IconComponent className="btn-icon" size={24} />
                  <span className="btn-text">{link.title}</span>
                  <ArrowIcon className="btn-arrow" size={18} />
                </a>
              );
            }
          })}
        </div>

        {/* Contacts Footer */}
        <footer className="wne-footer">
          <h2 className="contacts-title">Contacts</h2>
          <div className="contacts-grid">
            {(linksConfig.contacts || [])
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((contact, idx) => (
                <a
                  key={idx}
                  href={`mailto:${contact.email}`}
                  className="contact-card"
                  onClick={() => handleLinkClick('Contact', contact.email)}
                >
                  <div className="contact-name">{contact.name}</div>
                  <div className="contact-email">{contact.email}</div>
                </a>
              ))
            }
          </div>

          {/* Spacer Line */}
          <div className="footer-spacer"></div>

          {/* Admin Login Button */}
          <div className="admin-login-container">
            <a href="/login" className="admin-login-link">
              Admin Login
            </a>
          </div>
        </footer>
      </div>

      {/* Email Gate Modal */}
      {showEmailGate && currentLink && (
        <EmailGate
          onClose={() => {
            setShowEmailGate(false);
            setCurrentLink(null);
          }}
          onSubmit={handleEmailGateSubmit}
          resourceName={currentLink.gate_resource_name || currentLink.title}
          gateFields={currentLink.gate_fields || {
            name: { enabled: true, required: true },
            email: { enabled: true, required: true },
            phone: { enabled: false, required: false },
            linkedin: { enabled: false, required: false }
          }}
        />
      )}
    </div>
  );
};

export default WNEPage;
